import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import CustomButton from '../../components/CustomButton';
import EditableSection from '../../components/EditableSection';
import SingleDatePicker from '../../components/ui/SingleDatePicker';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateSeriesSeasonMutation } from '../../api/account/accountApiSlice';
import type { SeriesSeason } from '../../api/account/accountApiTypes';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import { formatDate } from '../../utils';
import { roomUseCasesOf } from '../../utils/roomUseCases';

const ISO_DATE = 'YYYY-MM-DD';

interface Props {
    readOnly: boolean;
    /** `tenant.config.seriesSeason` — null means unset. */
    seriesSeason: SeriesSeason | null;
    /** The tenant's active use cases; series are booked for the room family. */
    services: string[] | null;
    /** Master viewing another tenant: write that tenant by slug. */
    masterSlug?: string;
}

interface SeasonDraft {
    startDate: string | null;
    endDate: string | null;
}

/**
 * The season organisation owners may book series in (series booking, step 8;
 * `new-backend/admin/src/lib/series/seriesSeason.ts`). Explicit start and end
 * dates: while none is set, the booking app refuses series — the unset state
 * says so here rather than reading as "no limit".
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern). Removing the
 * season is part of editing: it empties both dates, and saving the empty pair
 * unsets it. A half-filled pair cannot be saved.
 */
const SeriesSeasonSection = ({ readOnly, seriesSeason, services, masterSlug }: Readonly<Props>) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [updateSeriesSeason, { isLoading: saving }] = useUpdateSeriesSeasonMutation();
    const saved: SeasonDraft = {
        startDate: seriesSeason?.startDate ?? null,
        endDate: seriesSeason?.endDate ?? null,
    };
    const draft = useEditableDraft(saved);

    if (roomUseCasesOf(services).length === 0) return null;

    const { startDate, endDate } = draft.value;
    const complete = !!startDate && !!endDate;
    const empty = !startDate && !endDate;

    const handleSave = async () => {
        try {
            await updateSeriesSeason({ startDate, endDate, slug: masterSlug }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('services.series_season.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t('services.series_season.error'), t),
                autoHideDuration: 5000,
            }));
        }
    };

    const language = i18n.language === 'en' ? 'en' : 'de';
    // From the draft, not the prop: right after saving, the draft holds the
    // saved season until the account refetch lands — the prop alone showed
    // "no season set" in that gap (staging 2026-10-13), as IdeaCardSection does.
    const view = complete ? (
        <dl className={'grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm'}>
            <div>
                <dt className={'text-foreground/60'}>{t('services.series_season.start')}</dt>
                <dd className={'text-foreground'}>{formatDate(startDate, language)}</dd>
            </div>
            <div>
                <dt className={'text-foreground/60'}>{t('services.series_season.end')}</dt>
                <dd className={'text-foreground'}>{formatDate(endDate, language)}</dd>
            </div>
        </dl>
    ) : (
        <p className={'text-sm text-foreground'}>{t('services.series_season.unset')}</p>
    );

    return (
        <EditableSection
            id={'series-season'}
            title={t('services.series_season.section')}
            description={t('services.series_season.section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            canSave={complete || empty}
            onEdit={draft.startEdit}
            onCancel={draft.cancel}
            onSave={() => { void handleSave(); }}
            view={view}
        >
            <div className={'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
                <SingleDatePicker
                    label={t('services.series_season.start')}
                    value={startDate ? dayjs(startDate) : null}
                    onChange={(value) => draft.setDraft({ ...draft.value, startDate: value?.format(ISO_DATE) ?? null })}
                    disabled={saving}
                />
                <SingleDatePicker
                    label={t('services.series_season.end')}
                    value={endDate ? dayjs(endDate) : null}
                    onChange={(value) => draft.setDraft({ ...draft.value, endDate: value?.format(ISO_DATE) ?? null })}
                    minDate={startDate ? dayjs(startDate) : undefined}
                    disabled={saving}
                />
            </div>
            {empty && <p className={'text-sm text-foreground'}>{t('services.series_season.unset')}</p>}
            {!empty && (
                <div>
                    <CustomButton
                        title={t('services.series_season.clear')}
                        isFilled={false}
                        onClick={() => draft.setDraft({ startDate: null, endDate: null })}
                        disabled={saving}
                    />
                </div>
            )}
        </EditableSection>
    );
};

export default SeriesSeasonSection;
